<template>
  <div
    ref="filmStripInteractiveHero"
    :class="
      bem({
        bg: props.block.background,
        hasTheme: !!props.block.theme,
        hasNavigation,
        hasScrolled,
        'reduced-height': (hasNavigation && isFirstOnPage)
      })
    "
    :style="`
      --progress: 0;
      --theme-bg: ${props.block.theme.buttonBackgroundColour.hex};
      --theme-text: ${props.block.theme.buttonTextColour.hex};
    `"
  >
    <div
      :class="
        bem('hero-content-wrapper', {
          'has-video-button': props.block.fullVideo,
        })
      "
    >
      <VisionLink
        v-if="showLogo"
        :to="resolveLocalePath('/')"
        title="SunGod Homepage"
        display="block"
        :class="bem('logo')"
        @click="handleLinkClick(`Home Logo`)"
      >
        <span class="sr-only">SunGod</span>
        <BaseLogo colour="white" />
      </VisionLink>
      <div
        v-if="block.textContent.title || block.textContent.subtitle"
        :class="bem('hero-content-inner')"
      >
        <VisionHeading
          v-if="block.textContent.title"
          :tag="isFirstOnPage ? 'h1' : 'h2'"
          size="h1"
          :class="bem('hero-content-title', { size: props.block.textSize })"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="formattedTitle" />
          <!-- eslint-enable vue/no-v-html -->
        </VisionHeading>
        <p
          v-if="block.textContent.subtitle"
          :class="bem('hero-content-subtitle')"
        >
          {{ block.textContent.subtitle }}
        </p>
        <div
          v-if="block.textContent.buttons?.length"
          :class="bem('hero-content-buttons')"
        >
          <VisionButton
            v-for="(button, buttonIndex) in buttons"
            :key="buttonIndex"
            size="regular"
            :to="button.url"
            :outline="button.style === 'outline'"
            :background-colour="button.colour || 'white'"
            @click="handleLinkClick(`Button - ${button.text}`)"
          >
            <template #content>
              {{ button.text }}
            </template>
          </VisionButton>
        </div>
      </div>
    </div>
    <MediaOverlayButton
      v-if="props.block.fullVideo"
      :data="props.block.fullVideo"
      :class="bem('full-video-cta')"
      :tracking-id="props.block.trackingId || ''"
      tracking-category="Film Strip Interactive Hero"
    />
    <div :class="bem('hero-video-wrapper')">
      <div :class="bem('hero-video-inner')">
        <BaseVideo
          :video="props.block.videoLandscape.video"
          :preview-start-time="0"
          :will-loop="true"
          :bg-video="true"
          :full-width="false"
          :class="bem('hero-video', { landscape: true })"
        />
        <BaseVideo
          v-if="props.block.videoPortrait?.video"
          :video="props.block.videoPortrait.video"
          :preview-start-time="0"
          :will-loop="true"
          :bg-video="true"
          :full-width="false"
          :class="bem('hero-video', { portrait: true })"
        />
      </div>
    </div>
    <div :class="bem('additional-media')">
      <div :class="bem('left-media')">
        <template
          v-for="item in props.block.additionalMedia.slice(0, 2)"
          :key="item.id"
        >
          <MediaItem
            :item="item"
            :class="bem('media-item')"
          />
        </template>
      </div>
      <div :class="bem('final-space')" />
      <div :class="bem('right-media')">
        <template
          v-for="item in props.block.additionalMedia.slice(2, 4)"
          :key="item.id"
        >
          <MediaItem
            :item="item"
            :class="bem('media-item')"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface FilmStripInteractiveHeroProps {
  rowPosition: number
  block: {
    id: string
    textContent: {
      title?: string
      subtitle?: string
      buttons?: {
        linkText: string
        link: any
        colour: 'white' | 'black'
        style: 'filled' | 'outline'
      }[]
    }
    textSize: string
    background: string
    theme?: any
    videoLandscape: any
    videoPortrait?: any
    videoPoster?: any
    additionalMedia: any[]
    fullVideo?: {
      mediaType: string
      video?: any
      externalVideo?: any
      text: string
      image: any
      theme?: any
    }
    trackingId?: string
  }
}

const { resolveLocalePath, buildInternalLink } = useUrlBuilder()
const props = defineProps<FilmStripInteractiveHeroProps>()
const bem = useBEM('FilmStripInteractiveHero')
const { gsap } = useGsap()
const { $track } = useNuxtApp() as any
const pageStore = usePageStore()
const filmStripInteractiveHero = ref<HTMLElement | null>(null)
const hasScrolled = ref<boolean>(false)

const isFirstOnPage = computed(() => {
  return props.rowPosition === 0
})

const hasNavigation = computed(() => {
  return !pageStore.hidePageHeader
})

const showLogo = computed(() => {
  return !hasNavigation.value && isFirstOnPage.value
})

const buttons = computed(() => {
  return props.block.textContent?.buttons?.map((button) => {
    return {
      text: button.linkText as string,
      url: buildInternalLink(button.link) as string,
      style: button.style as string,
      colour: button.colour as string,
    }
  })
})

const formattedTitle = computed(() => {
  if (!props.block.textContent.title) return ''

  const parts = props.block.textContent.title.split(/(\s+)/)
  let index = 0
  return parts
    .map((part) => {
      if (part.trim() === '') {
        return part
      } else {
        index++
        return `<span class="${bem(
          'title-span'
        )}" style="--word-index: ${index};"><span class="${bem(
          'title-transform'
        )}">${part}</span></span>`
      }
    })
    .join('')
})

onMounted(() => {
  if (filmStripInteractiveHero.value) {
    gsap.to(filmStripInteractiveHero.value, {
      scrollTrigger: {
        trigger: filmStripInteractiveHero.value,
        start: 'top -5%',
        end: 'bottom 75%',
        scrub: 0.5,
        // markers: true,
        onUpdate: (self) => {
          if (self.progress > 0.2) {
            hasScrolled.value = true
          } else {
            hasScrolled.value = false
          }
        },
      },
      '--progress': 1,
      ease: 'power1.out',
    })
  }
})

async function handleLinkClick(elementId: string) {
  await $track.global.linkClicked({
    interactionElement: elementId,
    interactionSection: props.block.trackingId,
    interactionCategory: 'Film Strip Interactive Hero',
    position: props.rowPosition + 1,
  }, 'Clicked Film Strip Interactive Hero Button')
}
</script>

<style lang="postcss">
.FilmStripInteractiveHero {
  --ratio-calculation: 0.8; /* 4/5 as a decimal */
  --full-height: 100dvh;
  --final-width: 30vw;
  --final-height: calc(var(--ratio-calculation) * var(--final-width));
  --offset-width: calc(0vw + (var(--progress) * (100vw - var(--final-width))));
  --offset-height: calc(
    0vh + (var(--progress) * (100vh - var(--final-height)))
  );

  @apply relative bg-white pb-10 h-[150vh] md:h-[200vh];

  @media (orientation: portrait) {
    --ratio-calculation: 1.25; /* 5/4 as a decimal */
    --final-width: 40vw;
  }
}

.FilmStripInteractiveHero__hero-video-wrapper {
  @apply sticky top-0 h-screen w-screen z-[2]
  flex flex-col justify-end items-center;
}

.FilmStripInteractiveHero__hero-video-inner {
  @apply relative object-cover
  max-h-full max-w-full;
  will-change: height, width;
  width: calc(100vw - var(--offset-width));
  height: calc(100vh - var(--offset-height));
  clip-path: inset(0 0 0 0 round calc(var(--progress) * 8px));
}

.FilmStripInteractiveHero__hero-video--portrait {
  @apply hidden;
}

/* Only hides landscape video if portrait video is set */
.FilmStripInteractiveHero__hero-video-inner:has(
    .FilmStripInteractiveHero__hero-video--portrait
  ) {
  @media (orientation: portrait) {
    .FilmStripInteractiveHero__hero-video--portrait {
      @apply block;
    }

    .FilmStripInteractiveHero__hero-video--landscape {
      @apply hidden;
    }
  }
}

.FilmStripInteractiveHero__hero-content-wrapper {
  @apply absolute h-[var(--full-height)] w-screen p-[var(--outer-gutter)]
  flex flex-col justify-end z-[3] text-white;

  @screen full {
    /* Position in line with global-wrapper-large */
    @apply global-wrapper-large;
    left: calc((100vw - var(--large-screen-size)) / 2);
  }
}

.FilmStripInteractiveHero__hero-content-buttons,
.FilmStripInteractiveHero__hero-content-subtitle {
  @apply transition-all duration-300 opacity-100 -translate-y-0 delay-200;
}

/* Make space for absolute positioned button */
.FilmStripInteractiveHero__hero-content-wrapper--has-video-button {
  .FilmStripInteractiveHero__hero-content-inner {
    @apply md:pr-[240px];
  }
}

.FilmStripInteractiveHero__hero-content-subtitle {
  @apply font-bold text-base lg:text-lg mb-6 delay-150;
}

.FilmStripInteractiveHero__title-span {
  @apply inline-block relative overflow-clip;
}

.FilmStripInteractiveHero__title-transform {
  @apply inline-block transition-transform translate-y-0 duration-300;
  transition-delay: calc(var(--word-index) * 0.05s);
}

.FilmStripInteractiveHero__full-video-cta {
  @apply absolute right-[var(--outer-gutter)] top-5 z-[3];
  --media-overlay-button-height: 96px;

  @screen md {
    --media-overlay-button-height: 120px;
    top: calc(var(--full-height) - var(--outer-gutter) - var(--media-overlay-button-height));
    transform: translate3d(
      0,
      calc(var(--progress) * (100vh - var(--final-height))),
      0
    );

    @media (orientation: portrait) {
      transform: translate3d(0, 0, 0);
    }
  }
}

.FilmStripInteractiveHero__logo {
  @apply absolute top-6 left-[var(--outer-gutter)] z-[2] w-[120px];
}

.FilmStripInteractiveHero__additional-media {
  @apply absolute bottom-10 overflow-hidden z-[1]
  flex items-center justify-center flex-nowrap
  max-w-[100vw];
}

.FilmStripInteractiveHero__right-media,
.FilmStripInteractiveHero__left-media {
  --transition-length: calc(var(--final-width) * 1.5);
  @apply h-[var(--final-height)] w-full flex gap-3;
}

.FilmStripInteractiveHero__right-media {
  
  .FilmStripInteractiveHero__media-item {
    transform: translate3d(
      calc(
        var(--transition-length) - (var(--progress) * var(--transition-length))
      ),
      0,
      0
    );
  }

  .FilmStripInteractiveHero__media-item:last-child {
    @apply mdMax:hidden;
    transform: translate3d(calc((var(--transition-length) * 1.25) - (var(--progress) * (var(--transition-length) * 1.25))), 0, 0);
  }
}

.FilmStripInteractiveHero__left-media {
  @apply justify-end;

  .FilmStripInteractiveHero__media-item {
    transform: translate3d(
      calc(
        (var(--transition-length) - (var(--progress) * var(--transition-length))) *
        -1
      ),
      0,
      0
    );
  }

  .FilmStripInteractiveHero__media-item:first-child {
    @apply mdMax:hidden;
    transform: translate3d(calc(((var(--transition-length) * 1.25) - (var(--progress) * (var(--transition-length) * 1.25))) * -1), 0, 0);
  }
}

.FilmStripInteractiveHero__final-space {
  @apply mx-1 md:mx-3
  w-[var(--final-width)]
  min-w-[var(--final-width)];
}

.FilmStripInteractiveHero__media-item {
  @apply relative h-full;
}

/** Modifiers
--dark: Background Colour
--reduced-height: Change viewport heights to make space for the nav
--has-theme: Change buttons etc. to theme colours
--has-scrolled: Animate out based on user scrolling past a point
*/
.FilmStripInteractiveHero--bg-dark {
  @apply bg-black;
}

.FilmStripInteractiveHero--reduced-height {
  --full-height: calc(100dvh - var(--nav-height) - var(--promo-bar-height));
}

.FilmStripInteractiveHero--has-theme {
  .VisionButton {
    --vision-button-text-colour: var(--theme-text, theme('colors.black'));
    --vision-button-border-colour: var(--theme-bg, theme('colors.white'));
    --vision-button-bg-colour: var(--theme-bg, theme('colors.white'));
    --vision-button-bg-colour--hover: var(--theme-bg, theme('colors.white'));
  }
}

.FilmStripInteractiveHero--has-scrolled {
  .FilmStripInteractiveHero__title-transform {
    @apply -translate-y-full;
  }

  .FilmStripInteractiveHero__hero-content-buttons,
  .FilmStripInteractiveHero__hero-content-subtitle {
    @apply opacity-0 -translate-y-full;
  }
}

/* Text Sizes */
.FilmStripInteractiveHero__hero-content-title {
  @apply text-3xl md:text-4xl mb-4 tracking-normal;

  &--size-xl {
    @apply text-6xl md:text-9xl md:leading-tighter;
  }

  &--size-large {
    @apply text-4xl md:text-6xl;
  }

  &--size-small {
    @apply text-xl md:text-2xl;
  }
}
</style>
